(() => {
  if (customElements.get('quiet-ritual-hero')) return;

  class QuietRitualHero extends HTMLElement {
    constructor() {
      super();
      this.mobileViewport = window.matchMedia('(max-width: 749px)');
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.visible = true;
      this.handoffRaf = 0;
      this.resizeRaf = 0;
      this.stableSizingRaf = 0;
      this.lateSizingTimer = 0;
      this.handoffAnimationRaf = 0;
      this.handoffSettleTimer = 0;
      this.isHandoffScrolling = false;
      this.isTouching = false;
      this.forwardHandoffPending = false;
      this.lastScrollY = window.scrollY;
      this.scrollDirection = 0;
      this.touchStartY = null;
      this.touchStartScrollY = 0;

      this.onVisibilityChange = this.onVisibilityChange.bind(this);
      this.onViewportChange = this.onViewportChange.bind(this);
      this.onTransitionScroll = this.onTransitionScroll.bind(this);
      this.onMotionPreferenceChange = this.onMotionPreferenceChange.bind(this);
      this.onTouchStart = this.onTouchStart.bind(this);
      this.onTouchEnd = this.onTouchEnd.bind(this);
    }

    connectedCallback() {
      this.videos = [...this.querySelectorAll('.dqr-hero__video')];
      this.desktopVideo = this.querySelector('.dqr-hero__video--desktop');
      this.mobileVideo = this.querySelector('.dqr-hero__video--mobile');
      this.classList.add('is-entered');
      this.classList.toggle('is-reduced-motion', this.isReducedMotion);

      this.videos.forEach((video) => {
        video.muted = true;
        video.defaultMuted = true;
        video.playsInline = true;
        video.addEventListener('playing', () => this.onVideoPlaying(video));
        video.addEventListener('error', () => this.onVideoError(video));
      });

      this.observer = new IntersectionObserver((entries) => {
        this.visible = this.isThemeEditor || (entries[0]?.isIntersecting ?? false);
        this.classList.toggle('is-paused', !this.isThemeEditor && (!this.visible || document.hidden));
        this.updatePlayback();
      }, { threshold: 0 });
      this.observer.observe(this);

      document.addEventListener('visibilitychange', this.onVisibilityChange);
      window.addEventListener('resize', this.onViewportChange, { passive: true });
      window.addEventListener('scroll', this.onTransitionScroll, { passive: true });
      window.visualViewport?.addEventListener('resize', this.onViewportChange, { passive: true });
      this.addEventListener('touchstart', this.onTouchStart, { passive: true });
      this.addEventListener('touchend', this.onTouchEnd, { passive: true });
      this.mobileViewport.addEventListener?.('change', this.onViewportChange);
      this.reduceMotion.addEventListener?.('change', this.onMotionPreferenceChange);

      this.setViewportSizing();
      this.scheduleStableViewportSizing();
      this.setNextSectionColor();
      this.lastScrollY = window.scrollY;
      this.updatePlayback();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      window.removeEventListener('resize', this.onViewportChange);
      window.removeEventListener('scroll', this.onTransitionScroll);
      window.visualViewport?.removeEventListener('resize', this.onViewportChange);
      this.removeEventListener('touchstart', this.onTouchStart);
      this.removeEventListener('touchend', this.onTouchEnd);
      this.mobileViewport.removeEventListener?.('change', this.onViewportChange);
      this.reduceMotion.removeEventListener?.('change', this.onMotionPreferenceChange);
      if (this.handoffRaf) cancelAnimationFrame(this.handoffRaf);
      if (this.resizeRaf) cancelAnimationFrame(this.resizeRaf);
      if (this.stableSizingRaf) cancelAnimationFrame(this.stableSizingRaf);
      if (this.lateSizingTimer) clearTimeout(this.lateSizingTimer);
      this.cancelHandoffScroll();
      this.videos?.forEach((video) => video.pause());
      this.nextSection?.classList.remove('dqr-handoff-next');
      this.sectionWrapper?.style.removeProperty('--dqr-top-offset');
      this.sectionWrapper?.style.removeProperty('--dqr-viewport-height');
      this.sectionWrapper?.style.removeProperty('--dqr-content-height');
    }

    get isMobile() {
      return this.mobileViewport.matches;
    }

    get isReducedMotion() {
      const query = new URLSearchParams(window.location.search);
      return this.reduceMotion.matches || query.get('dqr_motion') === 'reduce';
    }

    get isThemeEditor() {
      return Boolean(
        window.Shopify?.designMode ||
        document.documentElement.classList.contains('shopify-design-mode')
      );
    }

    get isIOSSafari() {
      const userAgent = navigator.userAgent || '';
      const isIOS = /iP(?:ad|hone|od)/.test(userAgent) ||
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      const isSafari = /Safari/i.test(userAgent) && !/(?:CriOS|FxiOS|EdgiOS|OPiOS)/i.test(userAgent);
      return isIOS && isSafari;
    }

    get activeVideo() {
      return this.isMobile ? this.mobileVideo : this.desktopVideo;
    }

    updatePlayback() {
      const active = this.activeVideo;
      const canPlay = Boolean(
        active &&
        this.dataset.liquidEnabled === 'true' &&
        !this.isReducedMotion &&
        (this.isThemeEditor || (this.visible && !document.hidden))
      );

      this.videos?.forEach((video) => {
        if (video !== active || !canPlay) video.pause();
      });

      if (!canPlay) {
        this.dataset.liquidRunning = 'false';
        this.dataset.liquidStatus = this.isReducedMotion ? 'reduced-motion' : 'paused';
        return;
      }

      const playPromise = active.play();
      if (playPromise?.catch) {
        playPromise.catch(() => {
          this.dataset.liquidRunning = 'false';
          this.dataset.liquidStatus = 'autoplay-blocked';
        });
      }
    }

    onVideoPlaying(video) {
      if (video !== this.activeVideo) {
        video.pause();
        return;
      }
      this.classList.add('is-liquid-ready');
      this.classList.remove('is-liquid-fallback');
      this.dataset.liquidLevel = 'video';
      this.dataset.liquidStatus = 'ready';
      this.dataset.liquidRunning = 'true';
    }

    onVideoError(video) {
      if (video !== this.activeVideo) return;
      this.classList.remove('is-liquid-ready');
      this.classList.add('is-liquid-fallback');
      this.dataset.liquidLevel = 'static';
      this.dataset.liquidStatus = 'video-error';
      this.dataset.liquidRunning = 'false';
    }

    onVisibilityChange() {
      this.classList.toggle('is-paused', !this.isThemeEditor && (document.hidden || !this.visible));
      this.updatePlayback();
    }

    onMotionPreferenceChange() {
      this.classList.toggle('is-reduced-motion', this.isReducedMotion);
      this.updatePlayback();
      this.updateHandoff();
    }

    onViewportChange() {
      if (this.isMobile && (this.isHandoffScrolling || window.scrollY > 4)) return;
      if (this.resizeRaf) return;
      this.resizeRaf = requestAnimationFrame(() => {
        this.resizeRaf = 0;
        this.setViewportSizing();
        this.updatePlayback();
        this.updateHandoff();
      });
    }

    scheduleStableViewportSizing() {
      if (this.stableSizingRaf) cancelAnimationFrame(this.stableSizingRaf);
      if (this.lateSizingTimer) clearTimeout(this.lateSizingTimer);

      this.stableSizingRaf = requestAnimationFrame(() => {
        this.stableSizingRaf = requestAnimationFrame(() => {
          this.stableSizingRaf = 0;
          this.setViewportSizing();
        });
      });

      this.lateSizingTimer = window.setTimeout(() => {
        this.lateSizingTimer = 0;
        this.setViewportSizing();
      }, 280);
    }

    setViewportSizing() {
      const sectionWrapper = this.sectionWrapper || this.closest('.shopify-section');
      if (!sectionWrapper) return;
      this.sectionWrapper = sectionWrapper;
      const documentTop = sectionWrapper.getBoundingClientRect().top + window.scrollY;
      const topOffset = Math.max(0, Math.min(160, Math.round(documentTop)));
      const visualHeight = Math.round(window.visualViewport?.height || window.innerHeight);
      const layoutHeight = Math.max(
        Math.round(window.innerHeight || 0),
        Math.round(document.documentElement?.clientHeight || 0)
      );
      const safariScreenHeight = this.isIOSSafari ? Math.round(window.screen?.height || 0) : 0;
      const viewportHeight = Math.max(visualHeight, layoutHeight, safariScreenHeight);
      sectionWrapper.style.setProperty('--dqr-top-offset', `${topOffset}px`);
      sectionWrapper.style.setProperty('--dqr-viewport-height', `${Math.max(560, viewportHeight - topOffset)}px`);
      sectionWrapper.style.setProperty('--dqr-content-height', `${Math.max(560, visualHeight - topOffset)}px`);
    }

    onTouchStart(event) {
      if (!this.isMobile || this.isReducedMotion || !this.nextSection) return;

      this.cancelHandoffScroll();
      this.isTouching = true;
      this.forwardHandoffPending = false;

      const touch = event.touches?.[0];
      if (!touch) return;
      this.touchStartY = touch.clientY;
      this.touchStartScrollY = window.scrollY;
    }

    onTouchEnd(event) {
      if (!this.isMobile || this.isReducedMotion || !this.nextSection || this.touchStartY === null) return;

      const touch = event.changedTouches?.[0];
      const deltaY = touch ? this.touchStartY - touch.clientY : 0;
      const nextTop = this.getNextSectionTop();
      const startedWithinHandoffZone = this.touchStartScrollY < nextTop * 0.38;
      const isBeforeNextSection = window.scrollY < nextTop - 24;

      this.touchStartY = null;
      this.isTouching = false;

      if (deltaY < 28 || !startedWithinHandoffZone || !isBeforeNextSection) {
        this.forwardHandoffPending = false;
        return;
      }

      this.forwardHandoffPending = true;
      this.scheduleHandoffSnap(true);
    }

    cancelHandoffScroll() {
      if (this.handoffAnimationRaf) cancelAnimationFrame(this.handoffAnimationRaf);
      if (this.handoffSettleTimer) clearTimeout(this.handoffSettleTimer);
      this.handoffAnimationRaf = 0;
      this.handoffSettleTimer = 0;
      this.isHandoffScrolling = false;
    }

    getNextSectionTop() {
      return this.nextSection.getBoundingClientRect().top + window.scrollY;
    }

    getHandoffTargetY() {
      const heading = this.nextSection.querySelector('.section-title-wrapper .heading, .section-title .heading, h2');
      const header = document.querySelector('custom-header.header, .header-wrapper custom-header, custom-header');
      const headingTop = heading
        ? heading.getBoundingClientRect().top + window.scrollY
        : this.getNextSectionTop();
      const headerHeight = header?.getBoundingClientRect().height || 0;
      return Math.max(0, Math.round(headingTop - headerHeight - 24));
    }

    startHandoffScroll(targetY) {
      if (this.handoffAnimationRaf) cancelAnimationFrame(this.handoffAnimationRaf);
      if (this.handoffSettleTimer) {
        clearTimeout(this.handoffSettleTimer);
        this.handoffSettleTimer = 0;
      }

      const startY = window.scrollY;
      const distance = targetY - startY;
      if (Math.abs(distance) < 1) return;

      const duration = Math.max(480, Math.min(1050, Math.abs(distance) * 1.7));
      const startTime = performance.now();
      this.isHandoffScrolling = true;

      const step = (now) => {
        const elapsed = Math.min(1, (now - startTime) / duration);
        const eased = elapsed < 0.5
          ? 4 * elapsed * elapsed * elapsed
          : 1 - Math.pow(-2 * elapsed + 2, 3) / 2;

        window.scrollTo(0, startY + distance * eased);

        if (elapsed < 1) {
          this.handoffAnimationRaf = requestAnimationFrame(step);
          return;
        }

        window.scrollTo(0, targetY);
        this.handoffAnimationRaf = 0;
        this.isHandoffScrolling = false;
        this.lastScrollY = targetY;
        this.updateHandoff();
      };

      this.handoffAnimationRaf = requestAnimationFrame(step);
    }

    onTransitionScroll() {
      const currentY = window.scrollY;
      const deltaY = currentY - this.lastScrollY;
      this.lastScrollY = currentY;

      if (deltaY < -0.5) {
        this.scrollDirection = -1;
        this.forwardHandoffPending = false;
        this.cancelHandoffScroll();
      } else if (deltaY > 0.5) {
        this.scrollDirection = 1;
      }

      if (this.handoffRaf) return;
      this.handoffRaf = requestAnimationFrame(() => {
        this.handoffRaf = 0;
        const shouldSnapForward = this.scrollDirection === 1;
        this.scrollDirection = 0;
        this.updateHandoff();
        this.scheduleHandoffSnap(shouldSnapForward);
      });
    }

    scheduleHandoffSnap(shouldSnapForward) {
      if (!shouldSnapForward) {
        if (this.handoffSettleTimer) clearTimeout(this.handoffSettleTimer);
        this.handoffSettleTimer = 0;
        return;
      }

      if (
        !this.isMobile ||
        this.isReducedMotion ||
        this.isTouching ||
        this.isHandoffScrolling ||
        !this.nextSection
      ) return;

      const currentY = window.scrollY;
      const nextTop = this.getNextSectionTop();
      const touchSettleLimit = nextTop + Math.min(180, window.innerHeight * 0.22);
      const handoffLimit = this.forwardHandoffPending ? touchSettleLimit : nextTop * 0.62;
      const isInHandoffZone = currentY > 18 && currentY < handoffLimit;

      if (!isInHandoffZone) {
        this.forwardHandoffPending = false;
        if (this.handoffSettleTimer) clearTimeout(this.handoffSettleTimer);
        this.handoffSettleTimer = 0;
        return;
      }

      if (this.handoffSettleTimer) clearTimeout(this.handoffSettleTimer);
      this.handoffSettleTimer = window.setTimeout(() => {
        this.handoffSettleTimer = 0;
        if (!this.isHandoffScrolling) {
          const targetY = this.getHandoffTargetY();
          this.forwardHandoffPending = false;
          this.startHandoffScroll(targetY);
        }
      }, 180);
    }

    updateHandoff() {
      if (!this.nextSection) return;
      if (this.isReducedMotion) {
        this.style.setProperty('--dqr-media-opacity', '1');
        this.style.setProperty('--dqr-transition-opacity', '1');
        this.dataset.handoffProgress = '1.000';
        return;
      }
      const travel = Math.max(140, Math.min(190, window.innerHeight * 0.2));
      const progress = Math.min(1, Math.max(0, window.scrollY / travel));
      this.style.setProperty('--dqr-media-opacity', (1 - progress * 0.08).toFixed(3));
      this.style.setProperty('--dqr-transition-opacity', (0.16 + progress * 0.84).toFixed(3));
      this.dataset.handoffProgress = progress.toFixed(3);
    }

    setNextSectionColor() {
      const sectionWrapper = this.sectionWrapper || this.closest('.shopify-section');
      const nextSection = sectionWrapper?.nextElementSibling;
      if (!nextSection) return;
      this.nextSection = nextSection;
      if (this.dataset.bottomTransition !== 'true') return;
      nextSection.classList.add('dqr-handoff-next');
      const candidates = [nextSection, ...[...nextSection.children].slice(0, 8)].filter(Boolean);
      const resolved = candidates
        .map((node) => getComputedStyle(node).backgroundColor)
        .find((color) => color && color !== 'rgba(0, 0, 0, 0)' && color !== 'transparent');
      if (resolved) this.style.setProperty('--dqr-next-bg', resolved);
      this.updateHandoff();
    }
  }

  customElements.define('quiet-ritual-hero', QuietRitualHero);
})();
